import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { persister } from 'lib/react-query/indexedDB';
import {
  CACHE_IDENTITY_KEY,
  bustCacheForIdentity,
  getCacheIdentity,
  readStoredCacheIdentity,
  writeStoredCacheIdentity,
} from 'lib/react-query/cacheIdentity';
import { useUser } from '../hooks/useUser';

/**
 * Clears the query cache when the tab discovers it is now a DIFFERENT user or organization
 * than the cache was built for — typically after a logout/login on the legacy side, which
 * swaps the shared cookie without this SPA reloading.
 */
export const CacheIdentityGuard = () => {
  const queryClient = useQueryClient();
  const { data: user } = useUser();
  const identity = getCacheIdentity(user?.loginContext?.id, user?.loginContext?.organizationId);

  const bustingRef = useRef<string | null>(null);

  useEffect(() => {
    // No resolved login context — logged out or still loading. "Don't know" is not "changed".
    if (!identity) return;
    if (bustingRef.current === identity) return;

    const stored = readStoredCacheIdentity();

    // First run on this browser: adopt the identity without busting
    if (stored === null) {
      writeStoredCacheIdentity(identity);
      return;
    }

    if (stored === identity) return;

    bustingRef.current = identity;
    void bustCacheForIdentity(queryClient, persister, identity);
  }, [identity, queryClient]);

  // Cross-tab propagation. `storage` fires only in OTHER tabs, so this is how a tab that has
  // not itself noticed anything finds out
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== CACHE_IDENTITY_KEY || !event.newValue) return;
      // Only react to a marker that disagrees with what THIS tab is currently showing.
      if (!identity || event.newValue === identity) return;

      bustingRef.current = event.newValue;
      queryClient.clear();
      // Best effort — the in-memory clear above is what stops stale data being shown. Wrapped
      // in an async IIFE because `removeClient` is Promisable<void> (may be sync), so a single
      // try/catch with await covers both a sync throw and a rejected promise.
      void (async () => {
        try {
          await persister.removeClient();
        } catch {
          // IndexedDB unavailable (private mode, quota, blocked).
        }
      })();
    };

    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [identity, queryClient]);

  return null;
};
